// ショップ（課金）：Stripe Checkout へ遷移。戻ってきたら「反映待ち」を出して /api/me を取り直す。
// 価格・購入済みの判定はサーバーの台帳が決める（ここは表示だけ）。
import { sfx } from '../audio';
import { api, type ShopProduct } from '../api';
import { products as metaProducts } from '../core-bridge';
import { go, register } from '../router';
import { CHARS, META_CONFIG, supportTickets, supportTicketsExpiry, type BuildingType, type CharId } from '@hyakki/core';
import { BUILDING_SPRITES, ITEM_CELL, faceStyle, gachaArt, itemIconStyle, omamoriIconStyle, spriteStyle } from '../assets';
import { artIcon, icon } from '../ui/icons';
import { hasEnt, refreshMe, state, subscribe } from '../state';
import { resumePendingOrder, setPendingOrder } from '../session';
import { esc, fmt, onAct } from '../ui/dom';
import { confirmDialog, toast } from '../ui/overlay';
import { errorText, loadInto } from '../ui/status';
import '../ui/fit';
import { playOmikuji } from '../ui/omikuji-fx';

const KIND: Record<string, string> = { permanent: '永続', consumable: '消耗品', cosmetic: '見た目', pass: '30日' };
let monthly: { until: number | null } | undefined;
// 外伝・見た目は作るまで出さない（core の UNRELEASED_PRODUCTS）
const CATS = [
  { id: 'limited', label: '限定' },
  { id: 'perm', label: '永続機能' },
  { id: 'item', label: '消耗品' },
] as const;
type Cat = (typeof CATS)[number]['id'];
let cat: Cat = 'perm';
const catOf = (p: ShopProduct): Cat | null =>
  // お守りの個別販売は終了（2026-10-14。お守りは霊玉くじ・章ボス・星報酬・図鑑・外伝で手に入る）
  p.offer ? 'limited' : p.id.startsWith('gaiden') || p.type === 'cosmetic' ? null : p.type === 'consumable' ? 'item' : 'perm';

register('shop', {
  title: 'ショップ',
  mount(el, params) {
    // Stripe からの戻り：?paid=<orderId>（/shop/complete から main.ts が変換）/ ?canceled=1
    const paid = params.get('paid');
    if (paid) {
      setPendingOrder(paid);
      resumePendingOrder();
    }
    if (params.get('canceled')) toast('購入をキャンセルしました');
    const want = params.get('cat');
    if (CATS.some((c) => c.id === want)) cat = want as Cat;
    // どの画面の案内から来たか（購入の注文に付けて、運営の売上で入口ごとに数える）
    const from = params.get('from') ?? 'shop';
    // ほかの画面の案内から来た：その商品まで送って光らせる（一度だけ）
    let focus = params.get('focus');
    el.innerHTML = `<div class="page fill shop-page"><section class="panel-paper deco shop">
      <header class="screen-head"><h1 class="screen-title">${artIcon('shop')}ショップ</h1><div class="end"><button class="link-btn" data-act="history">${icon('history')}購入履歴${icon('right')}</button></div></header>
      <div class="tabs seg-tabs shop-tabs" role="tablist">${CATS.map((c) => `<button class="tab-btn" role="tab" data-act="cat" data-c="${c.id}">${c.label}</button>`).join('')}</div>
      <div class="pending-slot"></div>
      <div class="shop-body fit-scroll"></div>
      <p class="legal small"><a href="/legal/tokushoho.html" target="_blank" rel="noopener">特定商取引法に基づく表記</a>・<a href="/legal/terms.html" target="_blank" rel="noopener">利用規約</a>　お支払いは Stripe の決済画面で行います。カード情報は当ゲームに送られません。霊玉は販売していません。</p>
    </section></div>`;
    const pending = () => {
      el.querySelector('.pending-slot')!.innerHTML = state.pendingOrder
        ? `<div class="pending-bar" role="status"><div class="spinner small"></div><div class="grow"><b>購入の反映を待っています</b><small>決済の確認が届きしだい反映されます。数分かかる場合があります。</small></div><button class="btn small" data-act="recheck">確認する</button></div>`
        : '';
    };
    pending();
    const body = el.querySelector<HTMLElement>('.shop-body')!;
    let list: (ShopProduct & { unavailable?: boolean })[] = [];
    let enabled = true;
    let testPurchase = false;
    const draw = () => {
      el.querySelectorAll<HTMLElement>('[data-c]').forEach((b) => {
        b.classList.toggle('on', b.dataset.c === cat);
        b.setAttribute('aria-selected', String(b.dataset.c === cat));
      });
      if (!list.length) return;
      // 限定の商品が無いときは「限定」のタブを出さない
      const hasLimited = list.some((p) => catOf(p) === 'limited');
      el.querySelector<HTMLElement>('[data-c="limited"]')!.hidden = !hasLimited;
      if (cat === 'limited' && !hasLimited) cat = 'perm';
      el.querySelectorAll<HTMLElement>('[data-c]').forEach((b) => b.classList.toggle('on', b.dataset.c === cat));
      const shown = list.filter((p) => catOf(p) === cat);
      const note = enabled ? (testPurchase ? '<p class="warn">テストサーバー：購入ボタンで決済なしにすぐ付与されます。</p>' : '') : '<p class="warn">現在、購入を受け付けていません（決済の準備中）。</p>';
      // 限定タブの見出し：リリース記念の間だけ（絵は art/ui/offer-header.webp）
      const set = cat === 'limited' ? shown.find((p) => p.id === 'release_set') : undefined;
      const head = set ? `<div class="offer-head" role="img" aria-label="リリース記念"><b>リリース記念</b><small>${offerLeft(set)}</small></div>` : '';
      body.innerHTML = `${note}${head}<ul class="products">${shown.map((p) => productHtml(p, enabled)).join('') || '<li class="muted">この分類の商品はありません。</li>'}</ul>`;
      const f = focus && body.querySelector<HTMLElement>(`.product[data-pid="${CSS.escape(focus)}"]`);
      if (f) {
        focus = null;
        f.classList.add('focus');
        f.scrollIntoView({ block: 'center' });
      }
    };
    const off = subscribe((ch) => {
      if (ch.has('pendingOrder')) pending();
      if (ch.has('entitlements') || ch.has('profile')) draw();
    });
    void loadInto(
      body,
      'ショップ',
      async () => {
        try {
          const r = await api.products();
          enabled = r.enabled;
          testPurchase = !!r.testPurchase;
          monthly = r.monthly;
          return r.products;
        } catch (e) {
          // 商品APIが使えないときは meta の台帳を「準備中」で表示する
          enabled = false;
          const m = metaProducts();
          if (m.length) return m.filter((p) => !p.offer).map((p) => ({ id: p.id, name: p.name, yen: p.yen, type: p.type, desc: p.desc, unavailable: true }));
          throw e;
        }
      },
      (ps) => {
        list = ps;
        // 分類の指定が無ければ、限定に買えるものがあるときは「限定」から。全部買い終えていれば（購入済み・一部購入済みだけ）「永続機能」から
        if (!want) {
          const buyable = list.some((p) => catOf(p) === 'limited' && !owned(p) && !p.partial);
          cat = buyable ? 'limited' : cat === 'limited' ? 'perm' : cat;
        }
        draw();
      },
    );
    const offAct = onAct(el, {
      buy: async (b) => {
        const p = list.find((x) => x.id === b.dataset.id);
        if (!p) return;
        const ok = await confirmDialog(
          `${p.name}\n税込 ${fmt(p.yen)}円\n${KIND[p.type] ?? ''}${p.type === 'permanent' ? '（買い切り・再ログインや別の端末でも有効）' : p.type === 'pass' ? `（買い切り・自動では更新しません${passUntil() ? `。${passUntil()}までの期限が30日延びます` : ''}）` : ''}\n\n${p.desc}\n\n対応環境：PC・スマートフォンのブラウザ\nStripe の決済画面へ移動します。`,
          { ok: '購入へ進む', title: '購入の確認' },
        );
        if (!ok) return;
        try {
          const r = await api.checkout(p.id, from);
          if (r.granted) {
            // テスト購入（テストサーバー・開発環境のみ）：決済せずにその場で付与済み
            await refreshMe();
            sfx('purchase');
            toast(`【テスト購入】${p.name} を付与しました`, 'good', 3000);
            // 本日残り（寄進・蔵出し）・限定の商品を取り直す
            list = (await api.products()).products;
            draw();
            if (r.result?.items?.length) await playOmikuji(r.result.items);
            return;
          }
          setPendingOrder(r.orderId);
          location.href = r.url!;
        } catch (err) {
          const map: Record<string, string> = { already_owned: '購入済みです', pass_full: '残りが10日を切ると延長できます', slots_max: '所持枠は上限です', sold_out_today: '本日分は売り切れました', kura_limit: '今日の分は買い終えました', offer_closed: '販売は終わりました', shop_disabled: '現在購入できません', guest: '購入するには、設定からGoogleと連携してください' };
          toast(map[(err as { code?: string }).code ?? ''] ?? errorText(err), 'error', 4000);
        }
      },
      history: () => go('purchases'),
      // ゲストは購入不可（ブラウザのデータが消えると買ったものに戻れない）。設定の Google 連携へ
      link: () => go('settings', { sec: 'account' }),
      cat: (b) => {
        cat = b.dataset.c as Cat;
        draw();
      },
      recheck: () => resumePendingOrder(true),
    });
    return () => {
      off();
      offAct();
    };
  },
});

/** チャットチケットの所持枚数と、いちばん近い期限（購入から180日。過ぎた分は消える） */
function chatHeld() {
  const p = state.profile!;
  const now = Date.now();
  const next = supportTicketsExpiry(p, now);
  const d = next ? new Date(next.until) : null;
  const n = supportTickets(p, now);
  const until = d ? `${d.getMonth() + 1}/${d.getDate()}まで` : '';
  return `<em class="p-kind">所持 ${fmt(n)}枚${d ? (next!.n === n ? `・${until}` : `・うち${fmt(next!.n)}枚は${until}`) : ''}</em>`;
}

/** 月パスの残り日数（期間中だけ。切り上げ） */
function passDaysLeft() {
  const left = (monthly?.until ?? 0) - Date.now();
  return left > 0 ? Math.ceil(left / 86_400_000) : null;
}

/** 月パスの期限（期間中だけ「M/D」） */
function passUntil() {
  const t = monthly?.until ?? 0;
  if (t <= Date.now()) return null;
  const d = new Date(t);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function owned(p: ShopProduct) {
  if (p.owned) return true;
  return hasEnt(p.id) || hasEnt(`omamori:${p.id.replace(/^om_/, '')}`);
}

function productHtml(p: ShopProduct & { unavailable?: boolean }, enabled: boolean) {
  // 月パスは期間中も買い足せる（期限が30日延びる）
  const have = p.type !== 'consumable' && p.type !== 'pass' && owned(p);
  if (p.full) return productShell(p, `<span class="btn wide owned-btn" aria-disabled="true">${p.slots ? '上限' : `${passUntil()}まで`}</span>`, true);
  const soldOut = p.remainingToday === 0;
  return productShell(
    p,
    have || p.partial
      ? `<span class="btn wide owned-btn" aria-disabled="true">${p.partial ? '一部購入済み' : '購入済み'}</span>`
      : p.unavailable || !enabled
        ? '<span class="btn wide owned-btn" aria-disabled="true">準備中</span>'
        : p.kura && !p.kura.koku
          ? '<span class="btn wide owned-btn" aria-disabled="true">準備中</span>'
          : soldOut
          ? `<span class="btn wide owned-btn" aria-disabled="true">${p.kura ? '本日購入済' : '本日売り切れ'}</span>`
          : state.me?.user.guest
            ? `<button class="btn primary wide" data-act="link">Google連携${icon('right', 'chev')}</button>`
            : `<button class="btn primary wide" data-act="buy" data-id="${esc(p.id)}">${p.type === 'pass' && !p.full && passUntil() ? `${passUntil()}まで・延長` : '購入'}${icon('right', 'chev')}</button>`,
    have || !!p.partial,
  );
}

function productShell(p: ShopProduct, action: string, have = false) {
  const name = p.name.replace(/（永続）$/, '');
  return `<li class="product ${have ? 'owned' : ''}" data-pid="${esc(p.id)}">
    <div class="p-art">${productArt(p)}</div>
    <b class="p-name">${esc(name)}</b>
    ${p.kura ? `<em class="p-kind">天下の石高（全体）${p.kura.koku}</em>` : ''}
    ${p.remainingToday != null ? `<em class="p-kind">本日残り${p.dailyGlobalCap ? '（全体）' : ''}${p.remainingToday}</em>` : ''}
    ${p.type === 'pass' && passDaysLeft() ? `<em class="p-kind">残り${passDaysLeft()}日</em>` : ''}
    ${p.offer ? `<em class="p-kind offer">${offerLeft(p)}</em>` : ''}
    ${p.effect?.kind === 'bundle' ? `<em class="p-kind">ばら ¥${fmt(bundleYen(p))}</em>` : ''}
    ${p.id === 'chat10' && state.profile ? chatHeld() : ''}
    ${p.slots ? `<em class="p-kind">所持枠 ${p.slots.cap}→${Math.min(p.slots.max, p.slots.cap + META_CONFIG.equipment.inventoryPerSlotPack)}</em>` : ''}
    ${p.desc ? `<p class="p-desc" title="${esc(p.desc)}">${esc(p.desc)}</p>` : '<p class="p-desc"></p>'}
    <span class="price">¥${fmt(p.yen)}</span>
    ${action}
  </li>`;
}

/** 限定の商品の締め切り：1回だけ／あと○分・○時間（2日未満）／M/Dまで */
function offerLeft(p: ShopProduct) {
  if (!p.offerUntil) return '1回だけ';
  const left = p.offerUntil - Date.now();
  if (left < 3_600_000) return `あと${Math.max(1, Math.ceil(left / 60_000))}分`;
  if (left < 48 * 3_600_000) return `あと${Math.floor(left / 3_600_000)}時間`;
  const d = new Date(p.offerUntil - 1);
  return `${d.getMonth() + 1}/${d.getDate()}まで`;
}

/** セットの中身をばらで買ったときの合計（meta の台帳の値段） */
function bundleYen(p: ShopProduct) {
  const keys = new Set(p.effect?.keys ?? []);
  return metaProducts()
    .filter((x) => x.effect.kind === 'entitlement' && keys.has(x.effect.key))
    .reduce((a, x) => a + x.yen, 0);
}

/** 商品の絵（既存の素材から） */
function productArt(p: ShopProduct): string {
  const id = p.id;
  if (id.startsWith('om_')) return `<span class="pa-om" style="${omamoriIconStyle(id.slice(3))}"></span>`;
  const char = /^(gaiden|skin)_(\w+)$/.exec(id)?.[2] as CharId | undefined;
  if (char && CHARS[char]) return `<span class="pa-face" style="${faceStyle(char)}"></span>${id.startsWith('gaiden') ? `<span class="pa-badge">${icon('scroll')}</span>` : ''}`;
  if (id.startsWith('design_')) {
    const sp = BUILDING_SPRITES[id.slice(7) as BuildingType];
    return `<span class="pa-bld" style="${sp ? spriteStyle(sp) : ''}"></span>`;
  }
  // 絵の上の数字は設定から（枠数・倍率を変えたら絵も合う）
  const C = META_CONFIG;
  const map: Record<string, string> = {
    gacha10: `<span class="pa-om" style="${gachaArt.omamori}"></span><span class="pa-big">10連</span>`,
    gacha10_first: `<span class="pa-om" style="${gachaArt.omamori}"></span><span class="pa-big">10連</span>`,
    gacha10_boss: `<span class="pa-om" style="${gachaArt.omamori}"></span><span class="pa-big">10連</span>`,
    release_set: '<span class="pa-om" style="background:url(/art/ui/offer-card.webp) center / contain no-repeat"></span><span class="pa-big">4点</span>',
    monthly: '<img class="pa-avatar" src="/art/player-avatars/15-kyubi.webp" alt=""><img class="pa-ring" src="/art/chat-frames/ring-01-kinzakura.webp" alt="">',
    exp2x: `<span class="pa-face" style="${faceStyle('akari')}"></span><span class="pa-big">EXP×${C.paid.exp2xMult}</span>`,
    auto: `<span class="pa-ic">${icon('auto')}</span>`,
    drop2x: `<span class="pa-item" style="${itemIconStyle(ITEM_CELL.chest)}"></span><span class="pa-big">×${C.rewards.drop2xMult}</span>`,
    recruit3x: `<span class="pa-ic">${icon('shiki')}</span><span class="pa-big">×${C.shikigami.recruit3xMult}</span>`,
    speed4: `<span class="pa-ic">${icon('speed')}</span><span class="pa-big">×${C.farm.speedPaid}</span>`,
    otasuke: `<span class="pa-ic">${icon('star')}</span>`,
    komedoiya: `<span class="pa-ic">${icon('swap')}</span>`,
    build_slots: `<span class="pa-ic">${icon('sp')}</span><span class="pa-big">${C.skills.presetSlots}枠</span>`,
    reserve_plus: `<span class="pa-ri"><i class="ri ri-hyoro"></i></span><span class="pa-big">+${C.stamina.paidReserveBonus}</span>`,
    rename: `<span class="pa-item" style="${itemIconStyle(ITEM_CELL.scroll)}"></span>`,
    slots: `<span class="pa-item" style="${itemIconStyle(ITEM_CELL.chest)}"></span>`,
    kishin: '<span class="pa-ri"><i class="ri ri-zeni"></i></span>',
    kuradashi: `<span class="pa-ri"><i class="ri ri-hyoro"></i></span><span class="pa-big">${C.kuradashi.hyoro}</span>`,
    chat10: '<img class="pa-avatar" src="/art/support/okami-smile.webp" alt=""><span class="pa-big">10枚</span>',
    voice: `<span class="pa-ic">${icon('chat')}</span>`,
  };
  return map[id] ?? `<span class="pa-ic">${icon('shop')}</span>`;
}

